import React, { useState, useMemo } from 'react';
import { Message } from 'semantic-ui-react';
import styled from 'styled-components';
import { useApiPortalV1GlossaryDataRetrieveQuery } from '../../../store/generated/portal/glossary';
import { MobilePageContent, Title, MobileSpinner } from '@/mobile/layouts/common/MobilePageStyles';
import { CardsGrid } from '@/mobile/layouts/common/MobilePageStyles';
import { SectionHeader, SectionDescription } from '@/mobile/layouts/common/MobilePageStyles';

interface PillarFactor {
  id: string;
  title: string;
  description?: string;
}

interface OutcomeGroup {
  id: string;
  name: string;
  description?: string;
}

interface GlossaryData {
  pillars: PillarFactor[];
  outcomes: OutcomeGroup[];
}

const MobileGlossaryPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState<'all' | 'pillars' | 'outcomes'>('all');

  const { data, isLoading, error } = useApiPortalV1GlossaryDataRetrieveQuery();

  const glossaryData = data as GlossaryData | undefined;

  const filteredData = useMemo(
    () => ({
      pillars: activeSection !== 'outcomes' ? glossaryData?.pillars : undefined,
      outcomes: activeSection !== 'pillars' ? glossaryData?.outcomes : undefined,
    }),
    [glossaryData, activeSection]
  );

  if (isLoading) {
    return (
      <MobilePageContent>
        <MobileSpinner text="Loading glossary..." />
      </MobilePageContent>
    );
  }

  if (error) {
    return (
      <MobilePageContent>
        <Message negative>
          <Message.Header>Error loading glossary</Message.Header>
          <p>Unable to load glossary data. Please try again later.</p>
        </Message>
      </MobilePageContent>
    );
  }

  if (!glossaryData?.pillars?.length && !glossaryData?.outcomes?.length) {
    return (
      <MobilePageContent>
        <Message info>
          <Message.Header>No Data Available</Message.Header>
          <p>There is no glossary data available at this time.</p>
        </Message>
      </MobilePageContent>
    );
  }

  const totalResults = (filteredData.pillars?.length ?? 0) + (filteredData.outcomes?.length ?? 0);

  return (
    <MobilePageContent>
      <ContentSection>
        {(filteredData.pillars?.length ?? 0) > 0 && (activeSection === 'pillars' || activeSection === 'all') && (
          <Section>
            <SectionHeader>
              <Title>Culture Glossary</Title>
            </SectionHeader>
            <SectionDescription>
              These pillars represent the core dimensions of organizational culture.
            </SectionDescription>
            <CardsGrid>
              {filteredData.pillars?.map((factor) => (
                <GlossaryCard key={factor.id}>
                  <CardHeader>
                    <CardTitle>{factor.title}</CardTitle>
                  </CardHeader>
                  <CardBody>
                    {factor.description || <EmptyText>No description available</EmptyText>}
                  </CardBody>
                </GlossaryCard>
              ))}
            </CardsGrid>
          </Section>
        )}

        {(filteredData.outcomes?.length ?? 0) > 0 && (activeSection === 'outcomes' || activeSection === 'all') && (
          <Section>
            <SectionHeader>
              <Title>Outcomes Glossary</Title>
            </SectionHeader>
            <SectionDescription>
              These outcomes are the organizational results the pillars are measured against.
            </SectionDescription>
            <CardsGrid>
              {filteredData.outcomes?.map((outcome) => (
                <GlossaryCard key={outcome.id}>
                  <CardHeader>
                    <CardTitle>{outcome.name}</CardTitle>
                  </CardHeader>
                  <CardBody>
                    {outcome.description || <EmptyText>No description available</EmptyText>}
                  </CardBody>
                </GlossaryCard>
              ))}
            </CardsGrid>
          </Section>
        )}

        {totalResults === 0 && (
          <Message info>
            <Message.Header>No results found</Message.Header>
            <p>Try adjusting your search terms or filters.</p>
          </Message>
        )}
      </ContentSection>
    </MobilePageContent>
  );
};

export default MobileGlossaryPage;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const Section = styled.div`
  background: white;
  border-radius: 8px;
`;

const GlossaryCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 20px 20px 0;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #111827;
`;

const CardBody = styled.div`
  padding: 16px 20px 20px;
  font-size: 0.95rem;
  color: #4b5563;
  line-height: 1.7;
`;

const EmptyText = styled.span`
  color: #6b7280;
  font-style: italic;
`;
